import type { FormDataAdapter } from "$lib/util/model-adapters"
import { PatchBuilder } from "$lib/util/model-adapters/patch.ts"
import {
  ACCOUNT_TYPE_CHOICES,
  INVESTMENT_OBJECTIVES,
  ValidateYup,
  type AccountOpeningApplication,
  type CustomerSuitability,
} from "@tastyworks/tastyworks-api"
import { number, object, string, type InferType } from "yup"

const MAX_TAX_BRACKET = 100
const MIN_TAX_BRACKET = 1

export const instrumentsKnowledgeSchema = object({
  experience: ValidateYup.customerSuitabilitySchema.pick([
    "stockTradingExperience",
    "coveredOptionsTradingExperience",
    "uncoveredOptionsTradingExperience",
    "futuresTradingExperience",
  ]),
})

export const instrumentsKnowledgeAdapter: FormDataAdapter<
  CustomerSuitability,
  InferType<typeof instrumentsKnowledgeSchema>["experience"]
> = {
  toForm: (suitability, formData) => {
    formData.coveredOptionsTradingExperience =
      suitability.coveredOptionsTradingExperience
    formData.uncoveredOptionsTradingExperience =
      suitability.uncoveredOptionsTradingExperience
    formData.futuresTradingExperience = suitability.futuresTradingExperience
    formData.stockTradingExperience = suitability.stockTradingExperience

    return formData
  },
  toModel: (formData, suitability) => {
    suitability.coveredOptionsTradingExperience =
      formData.coveredOptionsTradingExperience
    suitability.uncoveredOptionsTradingExperience =
      formData.uncoveredOptionsTradingExperience
    suitability.futuresTradingExperience = formData.futuresTradingExperience
    suitability.stockTradingExperience = formData.stockTradingExperience

    return suitability
  },
  toPatch: (formData) =>
    new PatchBuilder<CustomerSuitability>()
      .string(
        "coveredOptionsTradingExperience",
        formData.coveredOptionsTradingExperience
      )
      .string(
        "uncoveredOptionsTradingExperience",
        formData.uncoveredOptionsTradingExperience
      )
      .string("futuresTradingExperience", formData.futuresTradingExperience)
      .string("stockTradingExperience", formData.stockTradingExperience)
      .build(),
}

export const tradingObjectiveSchema = object({
  objective: object({
    tradingObjective: string()
      .oneOf(Object.values(INVESTMENT_OBJECTIVES))
      .required(),
  }),
})

export const tradingObjectiveAdapter = {
  toForm: (application, formData) => {
    formData.objective = {
      tradingObjective: application.investmentObjective ?? undefined,
    }
    return formData
  },
  toPatch: (data) =>
    new PatchBuilder<AccountOpeningApplication>()
      .string(
        "investmentObjective",
        data.objective.tradingObjective as INVESTMENT_OBJECTIVES
      )
      .build(),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof tradingObjectiveSchema>
  >,
  "toForm" | "toPatch"
>

export const financialInformationSchema = object({
  finance: ValidateYup.customerSuitabilitySchema
    .pick(["annualNetIncome", "netWorth", "liquidNetWorth"])
    .concat(
      object({
        businessTaxBracket: number().when(["accountType"], {
          is: ACCOUNT_TYPE_CHOICES.ENTITY,
          then: (schema) =>
            schema.required().min(MIN_TAX_BRACKET).max(MAX_TAX_BRACKET),
          otherwise: (schema) => schema.notRequired(),
        }),
        accountType: string()
          .oneOf(Object.values(ACCOUNT_TYPE_CHOICES))
          .required(),
      })
    ),
})

export const financialInformationAdapter: FormDataAdapter<
  CustomerSuitability,
  InferType<typeof financialInformationSchema>["finance"]
> = {
  toForm: (suitability, formData) => {
    formData.annualNetIncome = suitability.annualNetIncome ?? undefined
    formData.netWorth = suitability.netWorth ?? undefined
    formData.liquidNetWorth = suitability.liquidNetWorth ?? undefined
    if (suitability.taxBracket !== undefined) {
      formData.businessTaxBracket = Number(suitability.taxBracket) * 100
    }

    return formData
  },
  toModel: (formData, suitability) => {
    suitability.annualNetIncome = formData.annualNetIncome
    suitability.netWorth = formData.netWorth
    suitability.liquidNetWorth = formData.liquidNetWorth
    if (formData.businessTaxBracket !== undefined) {
      suitability.taxBracket = Number(formData.businessTaxBracket) / 100
    }

    return suitability
  },

  toPatch: (formData) =>
    new PatchBuilder<CustomerSuitability>()
      .number("annualNetIncome", formData.annualNetIncome)
      .number("netWorth", formData.netWorth)
      .number("liquidNetWorth", formData.liquidNetWorth)
      .number(
        "taxBracket",
        formData.businessTaxBracket === undefined
          ? undefined
          : Number(formData.businessTaxBracket) / 100
      )
      .build(),
}

export const tradingExperienceSchema = object({
  instrumentsKnowledge: instrumentsKnowledgeSchema,
  tradingObjective: tradingObjectiveSchema,
  financialInformation: financialInformationSchema,
})
